import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CompanyLogoIcon } from '../assets/companyLogoPlaceholder.svg';
import { getTenantConsents } from '../services/consents';
import { Button } from '../components/utils/Button';
import { Typography } from '../components/utils/Typography';

export default function SelectCompany() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchCompanies = async () => {
      try {
        const res = await getTenantConsents();
        const items = res.data?.data?.items || [];
        if (items.length === 0) {
          if (!cancelled) setError('No companies found.');
          return;
        }
        const seen = new Map();
        items.forEach((item) => {
          if (item.tenant_id && !seen.has(item.tenant_id)) {
            seen.set(item.tenant_id, item.tenant_name);
          }
        });
        const list = Array.from(seen, ([tenant_id, tenant_name]) => ({ tenant_id, tenant_name }));
        if (cancelled) return;
        localStorage.setItem('has_multiple_tenants', String(list.length > 1));
        if (list.length === 1) {
          const [company] = list;
          localStorage.setItem('tenant_id', company.tenant_id);
          if (company.tenant_name) localStorage.setItem('tenant_name', company.tenant_name);
          navigate(`/consents/${company.tenant_id}`, {
            state: { companyName: company.tenant_name },
            replace: true,
          });
          return;
        }
        setCompanies(list);
      } catch {
        if (!cancelled) setError('Failed to load companies. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchCompanies();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleContinue = () => {
    if (!selected) return;
    const company = companies.find((c) => c.tenant_id === selected);
    localStorage.setItem('tenant_id', selected);
    if (company?.tenant_name) localStorage.setItem('tenant_name', company.tenant_name);
    navigate(`/consents/${selected}`, { state: { companyName: company?.tenant_name } });
  };

  return (
    <div className="min-h-screen w-full bg-white flex flex-col md:flex-row">
      {/* Left — dark rounded card with Art.svg (desktop only) */}
      <div className="hidden md:flex shrink-0 p-8" style={{ width: '58%' }}>
        <img
          src="/loginArt.svg"
          alt="NYAI DPDP Guard"
          className="w-full h-full object-cover rounded-2xl"
          style={{ minHeight: 0 }}
        />
      </div>

      {/* Mobile logo */}
      <div className="flex md:hidden justify-center pt-24 pb-16">
        <img src="/assets/logo.svg" alt="NYAI" width={120} height={31} />
      </div>

      {/* Right — company selection panel */}
      <div className="flex flex-col md:justify-center flex-1 px-6 md:px-16 py-4 md:py-16">
        <div className="w-full md:mx-0" style={{ maxWidth: 480 }}>
          <Typography
            variant="h1"
            as="h2"
            className="md:text-4xl !font-bold mb-3 font-serif md:font-sans"
          >
            Select a Company
          </Typography>
          <Typography variant="body" color="muted" className="md:text-base mb-8 leading-relaxed">
            These are the companies that have your data and the consent for it. Select one to view its details.
          </Typography>

          {error && (
            <div className="mb-5 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-gray-400 text-sm">Loading...</div>
          ) : (
            !error && (
              <>
                <div
                  className="grid grid-cols-2 gap-4 mb-8 overflow-y-auto pr-1"
                  style={{ maxHeight: 360 }}
                >
                  {companies.map((company) => {
                    const isSelected = selected === company.tenant_id;
                    return (
                      <Button
                        key={company.tenant_id}
                        type="button"
                        variant="tertiary"
                        onClick={() => setSelected(company.tenant_id)}
                        className="!justify-start !gap-3 !px-4 !py-3.5 !rounded-xl border hover:!bg-transparent"
                        style={{
                          borderColor: isSelected ? '#2d7a7a' : '#e5e7eb',
                          borderWidth: isSelected ? 2 : 1,
                          background: isSelected ? '#f0f7f7' : '#fff',
                        }}
                      >
                        <span className="shrink-0 w-6 h-6 flex items-center justify-center">
                          <CompanyLogoIcon />
                        </span>
                        <span className="text-sm font-medium text-gray-800 truncate">
                          {company.tenant_name}
                        </span>
                      </Button>
                    );
                  })}
                </div>

                <Button
                  type="button"
                  disabled={!selected}
                  onClick={handleContinue}
                  className="w-full !py-3.5 !text-base"
                  style={{ background: '#2d7a7a' }}
                >
                  Continue
                </Button>
              </>
            )
          )}
        </div>
      </div>

      {/* Mobile footer */}
      <div className="flex md:hidden items-center justify-center gap-2 py-6 text-sm text-black font-serif">
        <span>Powered by</span>
        <img src="/assets/logo.svg" alt="NYAI" width={46} height={12} />
      </div>
    </div>
  );
}
